import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { TextInput } from "common/components/Input/TextInput";
import { DatePicker, DATE_PICKER_MODE } from "../Etc/calendar/DatePicker";
import { formatDateTime } from "common/utils/dateUtil";
import { CalendarIcon, IconWrapper } from "../icon";
import { useValidCheckSingle } from "common/hooks/validCheck";

export const DateInput = ({
  label,
  caption,
  name,
  placeholder,
  rules,
  disabled,
  readOnly,
  forceError,
  date: initDate,
  minDate,
  maxDate,
  disabledDate,
  initialMode = DATE_PICKER_MODE.CALENDAR,
  fixedMode = false,
  onSelect,
  onChangeYear,
  onChangeMonth,
  ...rest
}) => {
  const displayFormat =
    initialMode === DATE_PICKER_MODE.MONTH ? "YYYY-MM" : "YYYY-MM-DD";
  const {
    inputProps,
    error: fieldError,
    onValueChange,
  } = useValidCheckSingle({
    name,
    rules,
    defaultValue: initDate
      ? formatDateTime(initDate, displayFormat)
      : undefined,
  });

  const [selectedDate, setSelectedDate] = useState(() => {
    if (initDate) return initDate;
    if (inputProps.value) {
      const parsed =
        inputProps.value instanceof Date
          ? inputProps.value
          : new Date(inputProps.value);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return null;
  });

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (initDate) setSelectedDate(initDate);
  }, [initDate]);

  useEffect(() => {
    if (inputProps.value == null || inputProps.value === "") {
      setSelectedDate(null);
      return;
    }
    const parsed =
      inputProps.value instanceof Date
        ? inputProps.value
        : new Date(inputProps.value);
    if (isNaN(parsed.getTime())) return;

    const currentStr = selectedDate
      ? formatDateTime(selectedDate, displayFormat)
      : "";
    const nextStr = formatDateTime(parsed, displayFormat);
    if (currentStr !== nextStr) setSelectedDate(parsed);
  }, [inputProps.value]);

  // ── 바깥 클릭: wrapper와 popover 둘 다 벗어나면 닫기 ──
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inPopover = popoverRef.current?.contains(e.target);
      if (!inWrapper && !inPopover) setOpen(false);
    };
    const handleScroll = () => setOpen(false);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  const handleSelect = useCallback(
    (date) => {
      setSelectedDate(date);
      onValueChange(formatDateTime(date, displayFormat));
      onSelect?.(date);
      if (
        initialMode === DATE_PICKER_MODE.CALENDAR ||
        (initialMode === DATE_PICKER_MODE.MONTH && fixedMode)
      ) {
        setOpen(false);
      }
    },
    [initialMode, fixedMode, onSelect, onValueChange, displayFormat],
  );

  const handleInputClick = () => {
    if (disabled || readOnly) return;
    setOpen((prev) => !prev);
  };

  // ── paint 전에 popover 위치 확정 (DOM 직접 조작) ──
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!open || !el || !wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const popover = el.getBoundingClientRect();
    const margin = 16;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    let top;

    if (spaceBelow >= popover.height + margin) {
      top = rect.bottom + margin;
    } else if (spaceAbove >= popover.height + margin) {
      top = rect.top - popover.height - margin;
    } else {
      top = Math.max(margin, window.innerHeight - popover.height - margin);
    }

    let left = rect.left;
    if (left + popover.width > window.innerWidth - margin) {
      left = window.innerWidth - popover.width - margin;
    }
    if (left < margin) left = margin;

    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
    el.style.visibility = "visible";
  }, [open]);

  const displayValue = selectedDate
    ? formatDateTime(selectedDate, displayFormat)
    : "";

  return (
    <Wrapper ref={wrapperRef}>
      <ClickableArea onClick={handleInputClick}>
        <TextInput
          label={label}
          caption={caption}
          name={name}
          rules={rules}
          placeholder={placeholder}
          disabled={disabled}
          forceError={forceError}
          defaultValue={displayValue}
          value={displayValue}
          icon={{
            position: "right",
            element: (
              <IconWrapper>
                <CalendarIcon />
              </IconWrapper>
            ),
          }}
          onKeyDown={(e) => e.preventDefault()}
          {...rest}
        />
      </ClickableArea>

      {/* ── Portal: body에 직접 렌더링 ── */}
      {open &&
        createPortal(
          <PopoverWrap ref={popoverRef}>
            <DatePicker
              date={selectedDate || new Date()}
              minDate={minDate}
              maxDate={maxDate}
              disabledDate={disabledDate}
              initialMode={initialMode}
              fixedMode={fixedMode}
              onSelect={handleSelect}
              onChangeYear={onChangeYear}
              onChangeMonth={onChangeMonth}
            />
          </PopoverWrap>,
          document.body,
        )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ClickableArea = styled.div`
  cursor: pointer;
  & input {
    cursor: pointer;
  }
`;

const PopoverWrap = styled.div`
  position: fixed;
  z-index: 999;
  visibility: hidden;
  padding: 1rem;
  background: var(--color-background, #fff);
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: 0.75rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;
